import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Search, UserPlus, AlertCircle, Link2,
  ChevronLeft, ChevronRight, ArrowUpDown, Loader2, RefreshCw,
  Mail, MapPin, Phone, TrendingUp, Package, CreditCard,
  ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService, Customer, CustomerDetail, CustomerStats, UnlinkedCallawayInvoice } from '../services/api';

type Tab = 'list' | 'unlinked' | 'detail';

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const fmtDate = (d?: string) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const Customers: React.FC = () => {
  const [tab, setTab] = useState<Tab>('list');
  const [stats, setStats] = useState<CustomerStats | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('ltv');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDetail | null>(null);
  const [unlinked, setUnlinked] = useState<UnlinkedCallawayInvoice[]>([]);
  const [unlinkedLoading, setUnlinkedLoading] = useState(false);
  const [resolving, setResolving] = useState(false);

  // ─── Data Loading ─────────────────────────────────────────────────

  const loadStats = useCallback(async () => {
    const data = await apiService.getCustomerStats();
    setStats(data);
  }, []);

  const loadCustomers = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const result = await apiService.getCustomers({
        page, limit: 50, search: search || undefined, sortBy, sortDir,
      });
      setCustomers(result.data);
      setPagination(result.pagination);
    } catch {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [search, sortBy, sortDir]);

  const loadUnlinked = useCallback(async () => {
    setUnlinkedLoading(true);
    try {
      const data = await apiService.getUnlinkedCallawayInvoices();
      setUnlinked(data);
    } catch {
      toast.error('Failed to load unlinked invoices');
    } finally {
      setUnlinkedLoading(false);
    }
  }, []);

  const loadCustomerDetail = useCallback(async (id: string) => {
    const data = await apiService.getCustomer(id);
    if (data) {
      setSelectedCustomer(data);
      setTab('detail');
    } else {
      toast.error('Failed to load customer');
    }
  }, []);

  // Search only applies on Enter key

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  // ─── Actions ──────────────────────────────────────────────────────

  const handleSort = (col: string) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  const handleRunResolution = async () => {
    setResolving(true);
    try {
      const result = await apiService.runCustomerResolution();
      if (result) {
        toast.success(
          `Resolution complete: ${result.transactions?.created || 0} new customers, ${result.transactions?.resolved || 0} linked, ${result.callaway?.linked || 0} Callaway linked`
        );
        loadStats();
        loadCustomers();
      }
    } catch {
      toast.error('Resolution failed');
    } finally {
      setResolving(false);
    }
  };

  const handleLinkInvoice = async (customerId: string, invoiceId: string) => {
    const success = await apiService.linkCallawayInvoice(customerId, invoiceId);
    if (success) {
      toast.success('Invoice linked');
      loadUnlinked();
      loadStats();
    } else {
      toast.error('Failed to link invoice');
    }
  };

  // ─── Stats Cards ──────────────────────────────────────────────────

  const StatsCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Total Customers</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.totalCustomers?.toLocaleString() || 0}</p>
          </div>
          <Users className="h-8 w-8 text-blue-500 opacity-50" />
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Average LTV</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{fmtCurrency(stats?.avgLtv || 0)}</p>
          </div>
          <TrendingUp className="h-8 w-8 text-green-500 opacity-50" />
        </div>
      </div>
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">New This Month</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{stats?.newThisMonth?.toLocaleString() || 0}</p>
          </div>
          <UserPlus className="h-8 w-8 text-purple-500 opacity-50" />
        </div>
      </div>
      <button
        onClick={() => { setTab('unlinked'); loadUnlinked(); }}
        className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-orange-300 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Unlinked Callaway</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">{stats?.unlinkedCallawayCount || 0}</p>
          </div>
          <AlertCircle className="h-8 w-8 text-orange-500 opacity-50" />
        </div>
      </button>
    </div>
  );

  // ─── Customer List ────────────────────────────────────────────────

  const SortHeader: React.FC<{ col: string; label: string; className?: string }> = ({ col, label, className }) => (
    <th
      className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 ${className || ''}`}
      onClick={() => handleSort(col)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortBy === col && <ArrowUpDown className="h-3 w-3" />}
      </div>
    </th>
  );

  const CustomerList = () => (
    <div className="bg-white rounded-xl border border-gray-200">
      {/* Toolbar */}
      <div className="p-4 border-b border-gray-100 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setSearch(searchInput); }}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={handleRunResolution}
          disabled={resolving}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {resolving ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {resolving ? 'Resolving...' : 'Run Resolution'}
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <SortHeader col="full_name" label="Customer" />
              <SortHeader col="display_email" label="Email" />
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
              <SortHeader col="total_transactions" label="Transactions" />
              <SortHeader col="total_revenue" label="Revenue" />
              <SortHeader col="ltv" label="LTV" />
              <SortHeader col="first_purchase_date" label="First Purchase" />
              <SortHeader col="last_purchase_date" label="Last Purchase" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                </td>
              </tr>
            ) : customers.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-gray-400 text-sm">
                  {search ? 'No customers match your search' : 'No customers yet. Run resolution to build the customer database.'}
                </td>
              </tr>
            ) : customers.map(c => (
              <tr
                key={c.id}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => loadCustomerDetail(c.id)}
              >
                <td className="px-4 py-3">
                  <div className="font-medium text-sm text-gray-900">{c.full_name || '—'}</div>
                  {c.city && c.state && (
                    <div className="text-xs text-gray-400">{c.city}, {c.state}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{c.display_email || '—'}</td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                    {c.source_platform || '—'}
                  </span>
                  {c.source_channel && c.source_channel !== c.source_platform && (
                    <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                      {c.source_channel}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 text-right">{c.total_transactions}</td>
                <td className="px-4 py-3 text-sm text-gray-900 font-medium text-right">{fmtCurrency(c.total_revenue)}</td>
                <td className="px-4 py-3 text-sm font-bold text-right">
                  <span className={c.ltv >= 0 ? 'text-green-700' : 'text-red-600'}>{fmtCurrency(c.ltv)}</span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(c.first_purchase_date)}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{fmtDate(c.last_purchase_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => loadCustomers(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => loadCustomers(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="p-1.5 rounded border border-gray-200 disabled:opacity-30 hover:bg-gray-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ─── Unlinked Callaway Review ─────────────────────────────────────

  const UnlinkedReview = () => (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => setTab('list')} className="text-gray-400 hover:text-gray-600">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h3 className="font-semibold text-gray-900">Unlinked Callaway Invoices</h3>
          <span className="text-sm text-gray-500">({unlinked.length})</span>
        </div>
      </div>

      {unlinkedLoading ? (
        <div className="p-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
        </div>
      ) : unlinked.length === 0 ? (
        <div className="p-12 text-center text-gray-400 text-sm">
          All Callaway invoices are linked to customers.
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {unlinked.map(inv => (
            <UnlinkedInvoiceRow key={inv.id} invoice={inv} onLink={handleLinkInvoice} />
          ))}
        </div>
      )}
    </div>
  );

  // ─── Customer Detail ──────────────────────────────────────────────

  const DetailView = () => {
    if (!selectedCustomer) return null;
    const c = selectedCustomer;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => { setTab('list'); setSelectedCustomer(null); }} className="text-gray-400 hover:text-gray-600">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{c.full_name || 'Unknown'}</h2>
              <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                {c.display_email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{c.display_email}</span>}
                {c.phone && <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{c.phone}</span>}
                {c.city && c.state && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{c.city}, {c.state} {c.postal_code}</span>}
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500 uppercase">Lifetime Value</p>
            <p className={`text-2xl font-bold ${c.ltv >= 0 ? 'text-green-700' : 'text-red-600'}`}>{fmtCurrency(c.ltv)}</p>
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Source', value: c.source_platform || '—' },
            { label: 'Channel', value: c.source_channel || '—' },
            { label: 'First Purchase', value: fmtDate(c.first_purchase_date) },
            { label: 'Last Purchase', value: fmtDate(c.last_purchase_date) },
            { label: 'Customer Since', value: fmtDate(c.contact_created_date) },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className="text-sm font-medium text-gray-900 mt-0.5">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Transactions */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-gray-400" />
            <h3 className="font-semibold text-gray-900">Transactions</h3>
            <span className="text-sm text-gray-500">({c.transactions?.length || 0})</span>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Platform</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(c.transactions || []).map(tx => (
                  <tr key={tx.id} className={`text-sm ${tx.is_duplicate ? 'opacity-40' : ''}`}>
                    <td className="px-4 py-2 text-gray-500">{fmtDate(tx.created_at)}</td>
                    <td className="px-4 py-2 text-gray-600">{tx.platform?.account_name || '—'}</td>
                    <td className="px-4 py-2 text-gray-700 max-w-xs truncate">{tx.description || '—'}</td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        tx.type === 'payment' ? 'bg-green-50 text-green-700' :
                        tx.type === 'refund' ? 'bg-red-50 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>{tx.type}</span>
                    </td>
                    <td className={`px-4 py-2 text-right font-medium ${tx.type === 'refund' ? 'text-red-600' : 'text-gray-900'}`}>
                      {fmtCurrency(tx.amount)}
                    </td>
                  </tr>
                ))}
                {(!c.transactions || c.transactions.length === 0) && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-sm">No transactions</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Callaway Invoices */}
        {c.callaway_invoices && c.callaway_invoices.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-4 border-b border-gray-100 flex items-center gap-2">
              <Package className="h-4 w-4 text-gray-400" />
              <h3 className="font-semibold text-gray-900">Callaway Invoices</h3>
              <span className="text-sm text-gray-500">({c.callaway_invoices.length})</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Invoice #</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Type</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {c.callaway_invoices.map(inv => (
                    <tr key={inv.id} className="text-sm">
                      <td className="px-4 py-2 font-medium text-gray-900">{inv.invoice_number}</td>
                      <td className="px-4 py-2 text-gray-500">{fmtDate(inv.invoice_date)}</td>
                      <td className="px-4 py-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                          {inv.invoice_type}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          inv.status === 'Closed' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'
                        }`}>{inv.status}</span>
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-gray-900">{fmtCurrency(inv.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Notes */}
        {c.notes && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{c.notes}</p>
          </div>
        )}
      </div>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Customers</h1>
          <p className="text-sm text-gray-500 mt-1">Unified customer database with LTV tracking</p>
        </div>
      </div>

      <StatsCards />

      {tab === 'list' && <CustomerList />}
      {tab === 'unlinked' && <UnlinkedReview />}
      {tab === 'detail' && <DetailView />}
    </div>
  );
};

// ─── Subcomponents ────────────────────────────────────────────────

const UnlinkedInvoiceRow: React.FC<{
  invoice: UnlinkedCallawayInvoice;
  onLink: (customerId: string, invoiceId: string) => void;
}> = ({ invoice, onLink }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => setExpanded(!expanded)} className="text-gray-400 hover:text-gray-600">
            {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
          <div>
            <span className="font-medium text-sm text-gray-900">{invoice.invoice_number}</span>
            <span className="ml-2 text-sm text-gray-500">{invoice.customer_name}</span>
          </div>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
            {invoice.invoice_type}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-900">{fmtCurrency(invoice.amount)}</span>
          <span className="text-xs text-gray-400">{fmtDate(invoice.invoice_date)}</span>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 ml-9">
          {invoice.candidates.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider">Suggested Matches</p>
              {invoice.candidates.map(cand => (
                <div key={cand.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-3">
                    <div>
                      <span className="text-sm font-medium text-gray-900">{cand.full_name || '—'}</span>
                      {cand.display_email && (
                        <span className="ml-2 text-xs text-gray-500">{cand.display_email}</span>
                      )}
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      cand.confidence >= 0.85 ? 'bg-green-50 text-green-700' :
                      cand.confidence >= 0.60 ? 'bg-yellow-50 text-yellow-700' :
                      'bg-red-50 text-red-700'
                    }`}>{Math.round(cand.confidence * 100)}% match</span>
                  </div>
                  <button
                    onClick={() => onLink(cand.id, invoice.id)}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100"
                  >
                    <Link2 className="h-3 w-3" /> Link
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No candidate matches found. Create a new customer to link this invoice.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default Customers;
